import { JsonValue } from '../../Utility/JsonValue';

export enum Cache {
    GetWithFallback,
    GetAndUpdate,
}

export enum FulfillAction {
    NotCached,
    Cached,
    CachedAndModified,
}

export enum RejectAction {
    Retrieved,
    ErrorUnknownUrl,
    ErrorNoCachedData,
}

export interface UpdateFn {
    /**
     * Merge the cached data with the new response from the API.
     *
     * @param oldData - Pulled from cache. Will be `null` if not cached yet.
     * @param newData - From the API response.
     */
    (oldData: JsonValue, newData: JsonValue): Promise<JsonValue> | JsonValue;
}

export interface CacheConfigFn {
    /**
     * Mark URL to be cached.
     *
     * @param type - How the URL will be cached.
     * @param url
     * @param options - Additional options.
     * @param options.excludeParams - Parameter(s) that will be ignored when matching the URL.
     * @param options.update - (Used by {@link Cache.GetAndUpdate}) Function that will merge the
     *      old and new data.
     */
    (
        type: Cache,
        url: string,
        options?: {
            excludeParams?: Array<string>;
            update?: UpdateFn;
        }
    ): void;
}

export interface CacheConfiguration {
    /** How the URL will be cached. */
    type: Cache;
    url: string;
    /** Additional options. */
    options: {
        /** Parameter(s) that will be ignored when matching the URL. */
        excludeParams: Array<string>;
        /** (Used by {@link Cache.GetAndUpdate}) Function that will merge the old and new data. */
        update: null | UpdateFn;
    };
}

export interface CacheStoreFn {
    /**
     * Cache the given data.
     *
     * @param cacheId - The key used to store/retrieve the cached data.
     * @param data - The data to be cached.
     */
    (cacheId: string, data: JsonValue): Promise<void>;
}

export interface CacheRetrieveFn {
    /**
     * Retrieve cached data.
     *
     * @param cacheId - The key used to store/retrieve the cached data.
     * @throws `NotCachedError` if no cached data exists
     */
    (cacheId: string): Promise<JsonValue>;
}
